/* ═══════════════════════════════════════════════════════════════
   Forma paper — the DISPLAY half (read-only bands, End of Section, page vars).

   Page geometry comes from the ONE body-layout brain; the header,
   footer, pre-body and post-body bands come from the ONE zone-layout
   brain (shared/zone-layout.ts) with the same baked font metrics the
   Word export measures with. So the bands wrap and pair here exactly
   as they will in the .docx.

   This file never edits anything. The interactive band — every token a
   selectable, draggable, droppable object — is ZoneBandEdit.tsx, built on
   the SAME layout hook below so the two can never disagree about where a
   token sits. Read-only viewers, previews and exports render ZoneBand.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { StandardConfig, TextStyle } from '../../../shared/types';
import {
  layoutZonesIndexed,
  bakeTokenStyles,
  modAlign,
  parseTokenMods,
  type TokenTextCtx,
  type ZoneRow,
} from '../../../shared/zone-layout';
import { placeholderId } from '../../../shared/forma-placeholders';
import { makeZoneMeasure } from '../../lib/zone-measure';
import { resolvePageGeometry, inToPx, ptToPx } from '../../../shared/body-layout';
import { bandVariantName, positionalVariant } from '../formaZonesModel';
import {
  tokBase,
  tokModStyle,
  tokSpacing,
  parseLineParams,
  parseImageId,
  applyCaseModText,
} from '../../utils';

export interface PaperContext {
  forma: StandardConfig | null;
  specNumber: string;
  specName: string;
  /** Project/client/issuance values + resolved placeholders, already merged. */
  projectMeta: Record<string, string>;
  /** Master Forma Studio only: field tokens render as braced placeholders
   *  ("{## ## ##}", "{Spec Name}", "{Page #}") instead of sample values —
   *  a master is a template, and its canvas must never show text the export
   *  won't literally print. Project surfaces (Designer, formatted view,
   *  export preview) omit this and keep real values. */
  placeholderPreview?: boolean;
}

/** Format raw CSI digits per the forma's spec-number pattern ('## ## ##'). */
export function formatSpecNumber(raw: string, pattern?: string): string {
  const digits = (raw || '').replace(/\D/g, '');
  if (!pattern || !digits) return raw || '';
  let i = 0;
  const out = pattern.replace(/#/g, () => digits[i++] || '');
  return i < digits.length ? out + digits.slice(i) : out;
}

function formatDate(format?: string): string {
  const d = new Date();
  const mm = d.getMonth() + 1;
  const dd = d.getDate();
  const yyyy = d.getFullYear();
  switch (format) {
    case 'MMMM d, yyyy':
      return `${d.toLocaleDateString('en-US', { month: 'long' })} ${dd}, ${yyyy}`;
    case 'MM/dd/yyyy':
      return `${String(mm).padStart(2, '0')}/${String(dd).padStart(2, '0')}/${yyyy}`;
    case 'yyyy-MM-dd':
      return `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    default:
      return `${mm}/${dd}/${yyyy}`;
  }
}

export type ZoneKind = 'header' | 'footer' | 'pre' | 'post';

export interface ZoneBandProps extends PaperContext {
  kind: ZoneKind;
  /** Page number to resolve {Page} with. R1 renders one continuous sheet. */
  pageNum?: number;
  numPages?: number;
  /** Design mode: force which page variant's zones render (the variant being
      edited), instead of deriving it from pageNum. Additive — omitted, the
      Write-mode behaviour is exactly as before. */
  variant?: 'First' | 'Odd' | 'Even';
}

/** L/C/R — the three columns of a band, as the layout brain keys them. */
export type ZoneColumnKey = 'L' | 'C' | 'R';

type RowsSrc = ReturnType<typeof layoutZonesIndexed>['rowsSrc'];

/** The band's layout — zones, resolved tokens, wrapped rows, the rule — shared
 *  by the display band and the interactive one (ZoneBandEdit). */
export function useZoneBandLayout({
  kind,
  forma,
  specNumber,
  specName,
  projectMeta,
  placeholderPreview,
  pageNum = 1,
  numPages = 1,
  variant: variantOverride,
}: ZoneBandProps) {
  const doc = (forma?.docSettings || {}) as Record<string, unknown>;
  const hf = (forma?.headerFooter || {}) as Record<string, string[] | undefined>;
  const sp = (forma?.special || {}) as Record<string, unknown>;
  const images =
    (
      forma as unknown as {
        images?: Record<string, { width: number; height: number; mimeType: string; data: string }>;
      }
    )?.images || {};

  // Placeholder mode shows the forma's own number PATTERN ("## ## ##"),
  // braced — not a formatted sample — so the canvas reads as the template it
  // is. Both values feed ctx below, so the layout brain measures the exact
  // text the paper draws.
  const specNumFmt = placeholderPreview
    ? `{${(doc.specNumberFormat as string) || '## ## ##'}}`
    : formatSpecNumber(specNumber, doc.specNumberFormat as string | undefined);
  // AS-TYPED, never force-uppercased: the export brain (shared/zone-layout
  // tokenText + word-engine) prints {SpecName} exactly as entered — only the
  // `caps` MOD uppercases, and tokModStyle already renders that mod as CSS
  // text-transform. The paper's old unconditional toUpperCase showed caps the
  // .docx would never print (Josh, 2026-08-17).
  const specNameTxt = placeholderPreview ? '{Spec Name}' : specName || '';
  const geo = resolvePageGeometry(forma);

  const zones = React.useMemo(() => {
    if (kind === 'pre') {
      return {
        L: sp.bodyTopLeft as string[] | undefined,
        C: sp.bodyTopCenter as string[] | undefined,
        R: sp.bodyTopRight as string[] | undefined,
        keyL: 'bodyTopLeft',
        keyC: 'bodyTopCenter',
        keyR: 'bodyTopRight',
      };
    }
    if (kind === 'post') {
      if (sp.postBodyEnabled === false)
        return { L: [], C: [], R: [], keyL: 'postLeft', keyC: 'postCenter', keyR: 'postRight' };
      return {
        L: sp.postLeft as string[] | undefined,
        C: sp.postCenter as string[] | undefined,
        R: sp.postRight as string[] | undefined,
        keyL: 'postLeft',
        keyC: 'postCenter',
        keyR: 'postRight',
      };
    }
    // Word's own rule: page 1 only gets its own band when the forma asked for
    // a different first page. Otherwise page 1 prints the Odd band like every
    // other page — a forma with `firstPageDifferent: false` and an empty First
    // band must NOT render a blank header here.
    const variant =
      variantOverride ??
      bandVariantName(
        positionalVariant(pageNum, {
          firstPageDifferent: geo.firstPageDifferent,
          oddEvenDifferent: geo.oddEvenDifferent,
        })
      );
    return {
      L: hf[`${kind}${variant}Left`],
      C: hf[`${kind}${variant}Center`],
      R: hf[`${kind}${variant}Right`],
      keyL: `${kind}${variant}Left`,
      keyC: `${kind}${variant}Center`,
      keyR: `${kind}${variant}Right`,
    };
  }, [kind, hf, sp, pageNum, geo.oddEvenDifferent, geo.firstPageDifferent, variantOverride]);

  const ctx: TokenTextCtx = React.useMemo(
    () => ({
      specNumber: specNumFmt,
      specName: specNameTxt,
      endOfSectionText: sp.endOfSectionText as string | undefined,
      dateFormat: doc.dateFormat as string | undefined,
      project: projectMeta,
      placeholderMemories: Object.fromEntries(
        Object.entries(
          (forma as unknown as { placeholders?: Record<string, { was?: string }> })?.placeholders ||
            {}
        ).map(([id, p]) => [id, p?.was || ''])
      ),
      placeholderPreview,
    }),
    [
      specNumFmt,
      specNameTxt,
      sp.endOfSectionText,
      doc.dateFormat,
      projectMeta,
      forma,
      placeholderPreview,
    ]
  );

  const measure = React.useMemo(
    () =>
      makeZoneMeasure({
        sizePt: geo.bodyFontSizePt,
        family: geo.bodyFont,
        ctx,
        images,
      }),
    [geo.bodyFontSizePt, geo.bodyFont, ctx, images]
  );

  const resolveToken = (tok: string, key: number): React.ReactNode => {
    if (tok === '\n') return null;
    const base = tokBase(tok);
    const style = tokModStyle(tok) as React.CSSProperties;
    // A resolved token value can be multi-line (project tokens routinely are:
    // "Client Name\nCity, State"). The layout brain measures such a value by
    // its WIDEST line, so the paper must break it into lines too — rendered
    // as one long line it overruns its column and collides with its neighbour.
    const span = (rawTxt: string) => {
      // titlecase/sentencecase need the real string transform — CSS has no
      // sentence-case value, and `capitalize` doesn't lowercase source text
      // that's already stored ALL CAPS. `caps` stays CSS-driven via `style`.
      const txt = applyCaseModText(String(rawTxt), tok);
      const lines = txt.split('\n');
      if (lines.length === 1) {
        return (
          <span key={key} style={style}>
            {txt}
          </span>
        );
      }
      return (
        <span key={key} style={style}>
          {lines.map((ln, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {ln}
            </React.Fragment>
          ))}
        </span>
      );
    };
    switch (base) {
      case '{SpecNumber}':
        return span(specNumFmt);
      case '{SpecName}':
        return span(specNameTxt);
      case '{SpecNumName}':
        return span(`${specNumFmt} - ${specNameTxt}`);
      case '{Page}':
        return span(placeholderPreview ? '{Page #}' : String(pageNum));
      case '{NumPages}':
        return span(placeholderPreview ? '{Total Pages}' : String(numPages));
      case '{PageOfPages}':
        return span(placeholderPreview ? '{Page # of #}' : `Page ${pageNum} of ${numPages}`);
      case '{Date}':
        return span(
          placeholderPreview ? '{Date}' : formatDate(doc.dateFormat as string | undefined)
        );
      case '{Space}':
        return span(' ');
      default: {
        if (base.startsWith('{Image:')) {
          const id = parseImageId(tok);
          const img = id ? images[id] : null;
          if (!img) return null;
          const maxH = 42;
          return (
            <img
              key={key}
              src={`data:${img.mimeType};base64,${img.data}`}
              alt=""
              style={{ height: maxH, width: Math.round(img.width * (maxH / img.height)) }}
            />
          );
        }
        const phId = placeholderId(base);
        if (phId) {
          const val = projectMeta[phId] || '';
          if (val) return span(val);
          const was = (forma as unknown as { placeholders?: Record<string, { was?: string }> })
            ?.placeholders?.[phId]?.was;
          return (
            <span key={key} style={{ ...style }} className="dsg-token-unbound">
              {applyCaseModText(was || `‹${phId}›`, tok)}
            </span>
          );
        }
        if (base.startsWith('{ProjectToken:')) {
          const val = projectMeta[base.slice(1, -1)] || '';
          return val ? (
            span(val)
          ) : (
            <span key={key} className="dsg-token-unbound">
              ‹Project token›
            </span>
          );
        }
        const m = base.match(
          /^\{((?:Project|Client|Issuance|Team|Deliverable)(?:\.[A-Za-z]+)?)\}$/
        );
        if (m) {
          const val = projectMeta[m[1]] || '';
          return val ? (
            span(val)
          ) : (
            <span key={key} className="dsg-token-unbound">{`‹${m[1]}›`}</span>
          );
        }
        return span(base);
      }
    }
  };

  const noLines = (t?: string[]) => (t || []).filter((v) => !v.startsWith('{Line'));

  const styleStore = (kind === 'pre' || kind === 'post' ? sp : hf) as unknown as {
    tokenStyles?: Record<string, Record<number, { font?: string; align?: string }>>;
  };
  const bakedOf = (arr: string[] | undefined, zk: string) =>
    noLines(bakeTokenStyles(arr, styleStore.tokenStyles?.[zk]));
  const widthTwips = Math.max(1440, Math.round(geo.contentWidthIn * 1440));
  const { rows, rowsSrc } = layoutZonesIndexed(
    bakedOf(zones.L, zones.keyL),
    bakedOf(zones.C, zones.keyC),
    bakedOf(zones.R, zones.keyR),
    widthTwips,
    measure
  );

  const lineTok = [...(zones.L || []), ...(zones.C || []), ...(zones.R || [])].find((t) =>
    t.startsWith('{Line')
  );
  const rule = lineTok ? (
    <div
      className="dsg-zone-rule"
      style={{
        borderBottomWidth: Math.max(1, Math.round(parseLineParams(lineTok).thickness)),
        borderBottomColor: `#${parseLineParams(lineTok).color}`,
      }}
    />
  ) : null;

  const rowSpacing = (row: ZoneRow): React.CSSProperties => {
    let before = 0;
    let after = 0;
    [...row.L, ...row.C, ...row.R].forEach((t) => {
      const s = tokSpacing(t);
      if (s.before > before) before = s.before;
      if (s.after > after) after = s.after;
    });
    return { marginTop: ptToPx(before), marginBottom: ptToPx(after) };
  };

  const ruleBelow = kind === 'header' || kind === 'pre';

  return {
    zones,
    images,
    resolveToken,
    rows,
    rowsSrc,
    lineTok,
    rule,
    rowSpacing,
    ruleBelow,
  };
}

/** The band's rows, laid out side by side exactly as the layout brain packed
 *  them. `renderZone` draws one column's tokens on one row. */
export function renderBandRows(
  rows: ZoneRow[],
  rowsSrc: RowsSrc,
  rowSpacing: (row: ZoneRow) => React.CSSProperties,
  renderZone: (
    tokens: string[],
    col: ZoneColumnKey,
    origIdxs: (number | null)[],
    rowIdx: number
  ) => React.ReactNode
): React.ReactNode {
  return rows.map((row, ri) => {
    const occupied = [row.L, row.C, row.R].filter((z) => z.length > 0);
    const spacing = rowSpacing(row);
    const src = rowsSrc[ri];
    if (occupied.length === 0)
      return (
        <div key={ri} style={spacing}>
          &nbsp;
        </div>
      );
    if (occupied.length === 1) {
      const col: ZoneColumnKey = row.L.length ? 'L' : row.C.length ? 'C' : 'R';
      // A token's own Format Bar alignment wins over the column's.
      const own = row[col]
        .map((t) => (t === '\n' ? null : modAlign(parseTokenMods(t).mods)))
        .find((a) => a);
      const align = own ?? (row.C.length ? 'center' : row.R.length ? 'right' : 'left');
      return (
        <div key={ri} style={{ ...spacing, textAlign: align }}>
          {renderZone(row[col], col, src[col], ri)}
        </div>
      );
    }
    if (row.C.length > 0) {
      return (
        <div key={ri} style={{ ...spacing, position: 'relative' }}>
          <div style={{ textAlign: 'center', minHeight: '1em' }}>
            {renderZone(row.C, 'C', src.C, ri)}
          </div>
          {row.L.length > 0 && (
            <div style={{ position: 'absolute', left: 0, top: 0 }}>
              {renderZone(row.L, 'L', src.L, ri)}
            </div>
          )}
          {row.R.length > 0 && (
            <div style={{ position: 'absolute', right: 0, top: 0 }}>
              {renderZone(row.R, 'R', src.R, ri)}
            </div>
          )}
        </div>
      );
    }
    return (
      <div key={ri} style={{ ...spacing, display: 'flex', justifyContent: 'space-between' }}>
        <div>{renderZone(row.L, 'L', src.L, ri)}</div>
        <div style={{ textAlign: 'right' }}>{renderZone(row.R, 'R', src.R, ri)}</div>
      </div>
    );
  });
}

/**
 * One L/C/R band, laid out by the shared zone brain and rendered as rows that
 * are GUARANTEED to fit side by side — collision is structurally impossible
 * because the layout decided it, not CSS. Plain, inert text.
 */
export function ZoneBand(props: ZoneBandProps) {
  const { rows, rowsSrc, resolveToken, rule, rowSpacing, ruleBelow } = useZoneBandLayout(props);
  if (!rows.length && !rule) return null;
  const renderZone = (tokens: string[]): React.ReactNode => {
    const nodes: React.ReactNode[] = [];
    tokens.forEach((t, i) => {
      const node = resolveToken(t, i);
      if (node == null) return;
      const prev = i > 0 ? tokens[i - 1] : null;
      const needSpace =
        nodes.length > 0 &&
        prev !== '\n' &&
        tokBase(t) !== '{Space}' &&
        prev &&
        tokBase(prev) !== '{Space}';
      nodes.push(
        <React.Fragment key={i}>
          {needSpace ? ' ' : ''}
          {node}
        </React.Fragment>
      );
    });
    return nodes.length ? nodes : ' ';
  };
  return (
    <div className={`dsg-zone dsg-zone-${props.kind}`} aria-label={`${props.kind} band`}>
      {!ruleBelow && rule}
      {renderBandRows(rows, rowsSrc, rowSpacing, renderZone)}
      {ruleBelow && rule}
    </div>
  );
}

/** End of section — the closing line the forma prints.
 *  `onConfigure` restores the classic canvas's click-the-EOS-line-to-edit-it
 *  shortcut (`onEditSpecial('{EndOfSection}')`). Omitted → inert text, exactly
 *  as every read-only caller renders it today.
 *  `ownSpacing` (default true) lets the line apply its own before-spacing as
 *  `marginTop` — the read-only/preview behavior every existing caller keeps.
 *  The Studio canvas passes `false` because it renders that same gap as a
 *  draggable `ZoneSpacer` (region="eos") immediately above this element;
 *  leaving both on would double the gap. */
export function EndOfSection({
  forma,
  specNumber,
  specName,
  placeholderPreview,
  onConfigure,
  ownSpacing = true,
}: PaperContext & {
  onConfigure?: (mods?: { ctrl: boolean; shift: boolean }) => void;
  ownSpacing?: boolean;
}) {
  const sp = (forma?.special || {}) as Record<string, unknown>;
  if (sp.eosEnabled === false) return null;
  const doc = (forma?.docSettings || {}) as Record<string, unknown>;
  const text = (sp.endOfSectionText as string) || 'END OF SECTION';
  const specNumFmt = placeholderPreview
    ? `{${(doc.specNumberFormat as string) || '## ## ##'}}`
    : formatSpecNumber(specNumber, doc.specNumberFormat as string | undefined);
  const zs = sp.zoneSpace as { eos?: { before?: number } } | undefined;
  // §3.2 — WYSIWYG IS LAW: these are the same four properties word-engine's
  // eosRPr() emits, read from the same field. If the paper and the .docx
  // disagree about END OF SECTION, that is a bug by definition.
  const eos = sp.eosStyle as TextStyle | undefined;
  const rawBody = text
    .replace('{SpecNumber}', specNumFmt)
    .replace('{SpecName}', placeholderPreview ? '{Spec Name}' : specName || '');
  // Title / sentence need a real string transform (CSS has no sentence case);
  // 'upper' is Word's <w:caps/>, drawn as CSS uppercase.
  const body =
    eos?.textCase === 'title'
      ? applyCaseModText(rawBody, 'x|titlecase')
      : eos?.textCase === 'sentence'
        ? applyCaseModText(rawBody, 'x|sentencecase')
        : rawBody;
  const style: React.CSSProperties = {
    marginTop: ownSpacing
      ? ptToPx(zs?.eos?.before ?? (sp.eosSpacing as number) ?? (sp.postBodySpacing as number) ?? 24)
      : 0,
    ...(eos?.bold ? { fontWeight: 700 } : {}),
    ...(eos?.italic ? { fontStyle: 'italic' as const } : {}),
    ...(eos?.underline ? { textDecoration: 'underline' } : {}),
    ...(eos?.size ? { fontSize: ptToPx(eos.size) } : {}),
    ...(eos?.font ? { fontFamily: `"${eos.font.replace(/"/g, '')}"` } : {}),
    ...(eos?.color ? { color: `#${eos.color}` } : {}),
    ...(eos?.textCase === 'upper' ? { textTransform: 'uppercase' as const } : {}),
    ...(eos?.align ? { textAlign: eos.align } : {}),
  };
  if (!onConfigure) {
    return (
      <div className="dsg-eos" style={style}>
        {body}
      </div>
    );
  }
  return (
    <div
      className="dsg-eos dsg-eos-editable"
      role="button"
      tabIndex={0}
      title="Edit the end-of-section line"
      aria-label="End of section — click to edit"
      style={style}
      onClick={(e) => {
        e.stopPropagation();
        onConfigure({ ctrl: e.ctrlKey || e.metaKey, shift: e.shiftKey });
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onConfigure();
        }
      }}
    >
      {body}
    </div>
  );
}

/** The CSS custom properties that make the sheet the real page. */
export function paperVars(forma: StandardConfig | null): React.CSSProperties {
  const geo = resolvePageGeometry(forma);
  return {
    ['--dsg-page-w' as string]: `${inToPx(geo.pageWidthIn)}px`,
    // Page height at 100 % — the Pages view's page backdrop (zoom scales the
    // sheet, never this).
    ['--dsg-page-h' as string]: `${inToPx(geo.pageHeightIn)}px`,
    ['--dsg-margin-t' as string]: `${inToPx(geo.marginTopIn)}px`,
    ['--dsg-margin-b' as string]: `${inToPx(geo.marginBottomIn)}px`,
    ['--dsg-margin-l' as string]: `${inToPx(geo.marginLeftIn)}px`,
    ['--dsg-margin-r' as string]: `${inToPx(geo.marginRightIn)}px`,
    ['--dsg-body-font' as string]: geo.bodyFont,
    ['--dsg-body-size' as string]: `${ptToPx(geo.bodyFontSizePt)}px`,
    ['--dsg-line' as string]: String(geo.lineSpacing),
  };
}
